/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { contentModeCookieName } from "@insite/client-framework/Common/ContentMode";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import { IconMemo } from "@insite/mobius/Icon";
import { Button, Typography } from "@insite/mobius/index";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InfoCircleSolid from "@monsma/XDL/Icons/InfoCircleSolid";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// xng-change: adding mapStateToProps
const mapStateToProps = (state: ApplicationState) => ({
    userName: state.context.session.userName,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    signInUrl: getPageLinkByPageType(state, "SignInPage"),
});

export interface ProductDetailsViewStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerContainer?: GridContainerProps;
    topGridItem?: GridItemProps;
    middleGridItem?: GridItemProps;
    middleGridItemHidden?: HiddenProps;
    bottomGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridItemHidden?: HiddenProps;
    // xng-change
    productAvailabilityText?: TypographyPresentationProps;
}

export const viewStyles: ProductDetailsViewStyles = {
    leftColumnGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            overflow: hidden;
            direction: ltr;
        `,
    },
    topGridItem: { width: 12 },
    middleGridItem: { width: [12, 12, 0, 0, 0] },
    middleGridItemHidden: {
        above: "sm",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    bottomGridItem: { width: 12 },
    rightColumnGridItem: { width: [0, 0, 4, 4, 4] },
    rightColumnGridItemHidden: {
        below: "md",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    container: {
        css: css`
            direction: rtl;
        `,
    },
};

const styles = viewStyles;

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasHistory & HasProduct; // xng-change

const ProductDetailsView: React.FC<Props> = props => {
    const authorized = props.userName && !props.currentUserIsGuest; // xng-change
    const isCmsEditing = getCookie(contentModeCookieName) === "Editing"; // xng-change
    const product = props.product; // xng-change

    const handleClick = () => {
        // xng-change
        props.history.push(props.signInUrl?.url as any);
    };

    const unauthorizedTemplate = ( // xng-change
        <StyledWrapper>
            {product?.properties?.hidePrice !== "true" && (
                <StyledWrapper {...(styles as any).msrpPrice}>
                    <Typography variant="h3">{product.unitListPriceDisplay}</Typography>
                    {translate("MSRP")}
                </StyledWrapper>
            )}
            <StyledWrapper {...(styles as any).notAuthorizedMessage}>
                <IconMemo src={InfoCircleSolid} color="text.main" size={13} />
                <Typography {...styles.productAvailabilityText}>
                    {siteMessage("Custom_Product_Availability_Purchase_Price")}
                </Typography>
            </StyledWrapper>
            <Typography variant="p" {...(styles as any).notAuthorizedNotice}>
                {siteMessage("Custom_Unauthorized_Users_Notice")}
            </Typography>
            <Button variant="primary" sizeVariant="medium" onClick={handleClick}>
                {translate("Sign In / Register")}
            </Button>
        </StyledWrapper>
    );

    // xng-change: update the template to match out needs
    return (
        <GridContainer {...styles.container}>
            <GridItem
                {...styles.rightColumnGridItem}
                data-test-selector="productDetails_rightColumn"
                className={`${!authorized && "not-authorized"}`}
            >
                <Hidden {...styles.rightColumnGridItemHidden}>
                    {(isCmsEditing || authorized) && <Zone contentId={props.id} zoneName="Content3" />}
                    {(isCmsEditing || !authorized) && unauthorizedTemplate}
                </Hidden>
            </GridItem>
            <GridItem {...styles.leftColumnGridItem} data-test-selector="productDetails_leftColumn">
                <GridContainer {...styles.leftColumnInnerContainer}>
                    <GridItem {...styles.topGridItem}>
                        <StyledWrapper {...(styles as any).imageInfoWrapper}>
                            <StyledWrapper {...(styles as any).imageWrapper}>
                                <Zone contentId={props.id} zoneName="Content00" />
                            </StyledWrapper>
                            <StyledWrapper {...(styles as any).infoWrapper}>
                                <Zone contentId={props.id} zoneName="Content0" />
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.middleGridItem}>
                        <Hidden {...styles.middleGridItemHidden}>
                            {(isCmsEditing || authorized) && <Zone contentId={props.id} zoneName="Content1" />}
                            {(isCmsEditing || !authorized) && unauthorizedTemplate}
                        </Hidden>
                    </GridItem>
                    <GridItem {...styles.bottomGridItem}>
                        <Zone contentId={props.id} zoneName="Content2" />
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(withProduct(ProductDetailsView))), // xng-change
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
